/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import type ApplicationInstance from "@ember/application/instance";
import { action } from "@ember/object";
import { getOwner } from "@ember/owner";
import type RouterService from "@ember/routing/router-service";
import { service } from "@ember/service";
import type { BufferedChangeset } from "ember-changeset/types";
import type ApplicationAdapter from "parog-web/adapters/application";
import type { ForcBaseArgs } from "parog-web/components/forc/abstract/base-component";
import CrudJournalEventBaseComponent from "parog-web/components/forc/abstract/crud-journal-event-based-component";
import type { ToastMessage } from "parog-web/components/forc/abstract/journal-event-based-component";
import DraftManager from "parog-web/components/forc/draft-manager";
import JournalEventRetrievalStrategies from "parog-web/components/forc/utils/journal-event-retrieval-strategies";
import BioExamResultsReceivedJournalEvent from "parog-web/models/bio-exam-results-received-journal-event";
import type File from "parog-web/models/file";
import type FileAttachment from "parog-web/models/file-attachment";
import type Patient from "parog-web/models/patient";
import type AnalyticsService from "parog-web/services/analytics";
import type NavigationService from "parog-web/services/navigation";
interface Args extends ForcBaseArgs {
  changeset: BufferedChangeset;
  model: BioExamResultsReceivedJournalEvent;
  patient: Patient;
}

export default class BioExamResultsNewEditionFormComponent extends CrudJournalEventBaseComponent<
  BioExamResultsReceivedJournalEvent,
  Args
> {
  @service declare analytics: AnalyticsService;
  @service declare router: RouterService;
  @service declare navigation: NavigationService;

  applicationAdapter = (getOwner(this) as ApplicationInstance).lookup(
    "adapter:application"
  ) as ApplicationAdapter;

  static forcDisplayName = "Résultats biologiques";
  static fullComponentId = "bio-exam-results/new-edition-form";

  static journalEventClass = BioExamResultsReceivedJournalEvent;
  retrieveJournalEvent = JournalEventRetrievalStrategies.sourceJournalEvent;

  get changeset() {
    this.draft = new DraftManager(this);
    return this.args.changeset;
  }

  get toastMessage(): ToastMessage {
    return {
      message: "Vos résultats biologiques ont bien été pris en compte.",
      title: "Biologie"
    };
  }

  @action
  async save(): Promise<BioExamResultsReceivedJournalEvent> {
    this.changeset.set("normalized", true);
    // Need to persist this.isNewMode because even if it a new model, after `super.save()` this.isNewMode turn to false
    const isNewMode = this.isNewMode;
    const model = (await super.save()) as BioExamResultsReceivedJournalEvent;
    if (isNewMode) {
      await this.store
        .createRecord("patient-chat-message-sent-journal-event", {
          occurredAt: new Date(),
          triggeringJournalEvent: model,
          patient: model.patient,
          creator: model.creator,
          body: "Résultats d'examen envoyés"
        })
        .save();

      await fetch(
        `/api/v1/task_planned_journal_event/${model.patient.id}/create_bio_task`,
        {
          method: "POST",
          headers: Object.assign(
            {
              "Content-Type": "application/json"
            },
            this.applicationAdapter.headers
          )
        }
      );
    }

    if (this.args.afterCompleted) this.args.afterCompleted();

    return model;
  }

  /**
   * Callback function which is given
   * to the File upload component.
   * Will be called within the component
   * when upload is successful.
   */
  @action
  onSelected(file: File): void {
    const files = this.changeset.get("files");
    const isFirstSelectedFile = Boolean(!files);

    if (isFirstSelectedFile) {
      this.changeset.set("files", [file]);
    } else {
      files.push(file);
      this.changeset.set("files", files);
    }
  }

  /**
   * Callback function which is given
   * to the File upload component.
   * Will be called within the component
   * when a file is deleted.
   */
  @action
  destroyFile(index: number): void {
    const files = this.changeset.get("files");
    files?.splice(index, 1);
    this.changeset.set("files", files);
  }

  /**
   * File upload accepted types.
   * When upload button is clicked and
   * file explorer opens, only those
   * types would be uploadable.
   */
  get acceptedTypes(): Array<string> {
    return [
      "application/pdf",
      "image/jpeg",
      "image/jpg",
      "image/png",
      "application/rtf"
    ];
  }

  @action
  back() {
    this.navigation.back();
  }

  get fileAttachments(): Array<FileAttachment> {
    return (
      (this.changeset.orderedFileAttachments as Array<FileAttachment>) || []
    );
  }
}
